/**
 * Kanban (R4) - sloupce = brokerage stage (packages/case/src/internal/state-machine.ts
 * `BrokerageStage`, jen ceske popisky pro zobrazeni). Poradi je kosmeticke
 * (leva->prava dle pipeline); SKUTECNE povolene prechody chodi z API
 * (`allowedTransitions` na CaseDto) - tady se state machine neduplikuje.
 *
 * INVARIANT: seznam musi obsahovat KAZDOU fazi stavoveho automatu, jinak
 * zakazka v te fazi ze seznamu zmizi. Fazi `audit` pridal krok S1 (spusteni
 * auditu prevede zakazku z `intake`) a krok S3 ji sem doplnil — bez ni byla
 * zakazka hned po spusteni auditu neviditelna (nalez z e2e prubehu).
 */
export const KANBAN_STAGE_ORDER = [
  'intake',
  'audit',
  'preparation',
  'listing',
  'viewings',
  'negotiation',
  'reservation',
  'contracting',
  'handover',
  'closed',
] as const;

export const CANCELLED_STAGE = 'cancelled';

export const STAGE_LABELS: Readonly<Record<string, string>> = {
  intake: 'Příjem',
  audit: 'Audit',
  preparation: 'Příprava',
  listing: 'Inzerce',
  viewings: 'Prohlídky',
  negotiation: 'Jednání',
  reservation: 'Rezervace',
  contracting: 'Smlouvy',
  handover: 'Předání',
  closed: 'Uzavřeno',
  cancelled: 'Zrušené',
};

export function stageLabel(stage: string): string {
  return STAGE_LABELS[stage] ?? stage;
}

/**
 * Krok O8: barevny stitek faze pro seznam a tabulku. V kanbanu nese fazi
 * sloupec, v jednosloupcovych zobrazenich ji musi nest stitek u radku.
 * Barva je redundantni indikator - popisek faze je vzdy vypsany textem.
 */
const STAGE_BADGE_CLASS: Readonly<Record<string, string>> = {
  intake: 'rh-badge--neutral',
  audit: 'rh-badge--neutral',
  preparation: 'rh-badge--neutral',
  listing: 'rh-badge--accent',
  viewings: 'rh-badge--accent',
  negotiation: 'rh-badge--accent',
  reservation: 'rh-badge--warning',
  contracting: 'rh-badge--warning',
  handover: 'rh-badge--warning',
  closed: 'rh-badge--success',
  cancelled: 'rh-badge--danger',
};

export function stageBadgeClass(stage: string): string {
  return STAGE_BADGE_CLASS[stage] ?? 'rh-badge--neutral';
}

/**
 * Krok UXR1 — tytez barvy ve stitcich noveho vzhledu (`rh-a2-badge--*`,
 * mockup `02-zakazky.html`). Mapovani je odvozene z `STAGE_BADGE_CLASS`, aby
 * se obe sady nemohly rozejit: faze ma jednu barvu, at kresli kterykoli
 * zobrazovaci styl.
 */
const BADGE_CLASS_A2: Readonly<Record<string, string>> = {
  'rh-badge--neutral': '',
  'rh-badge--accent': 'rh-a2-badge--accent',
  'rh-badge--warning': 'rh-a2-badge--wait',
  'rh-badge--success': 'rh-a2-badge--ok',
  'rh-badge--danger': 'rh-a2-badge--bad',
};

export function stageBadgeClassA2(stage: string): string {
  return BADGE_CLASS_A2[stageBadgeClass(stage)] ?? '';
}

/** Barevna tecka v zahlavi sloupce kanbanu (mockup `.kcol__head .dot`). */
const DOT_CLASS_A2: Readonly<Record<string, string>> = {
  'rh-badge--neutral': '',
  'rh-badge--accent': 'rh-a2-dot--accent',
  'rh-badge--warning': 'rh-a2-dot--wait',
  'rh-badge--success': 'rh-a2-dot--ok',
  'rh-badge--danger': 'rh-a2-dot--bad',
};

export function stageDotClassA2(stage: string): string {
  return DOT_CLASS_A2[stageBadgeClass(stage)] ?? '';
}

/** Kanban je scenar pro brokerage pipeline (viz zadani) - acquisition/pm maji vlastni stavovy prostor. */
export const KANBAN_CASE_TYPES = ['brokerage', 'brokerage_sale', 'brokerage_rent'] as const;
export type KanbanCaseType = (typeof KANBAN_CASE_TYPES)[number];

export const CASE_TYPE_LABELS: Readonly<Record<KanbanCaseType, string>> = {
  brokerage: 'Obecné',
  brokerage_sale: 'Prodej',
  brokerage_rent: 'Pronájem',
};

export type SlaStatus = 'ok' | 'blizi_se' | 'prosle' | 'none';

const SLA_WARNING_WINDOW_MS = 2 * 24 * 60 * 60 * 1000;

export function slaStatus(
  slaDueAt: string | null,
  slaBreachedAt: string | null,
  closedAt: string | null,
  now: Date,
): SlaStatus {
  if (closedAt !== null || slaDueAt === null) return 'none';
  if (slaBreachedAt !== null) return 'prosle';
  const due = new Date(slaDueAt);
  if (due.getTime() < now.getTime()) return 'prosle';
  if (due.getTime() - now.getTime() <= SLA_WARNING_WINDOW_MS) return 'blizi_se';
  return 'ok';
}

export const SLA_STATUS_LABELS: Readonly<Record<SlaStatus, string>> = {
  ok: 'SLA OK',
  blizi_se: 'SLA se blíží',
  prosle: 'SLA prošlo',
  none: '',
};

export const SLA_STATUS_BADGE_CLASS: Readonly<Record<SlaStatus, string>> = {
  ok: 'rh-badge--success',
  blizi_se: 'rh-badge--warning',
  prosle: 'rh-badge--danger',
  none: 'rh-badge--neutral',
};

/** Krok UXR1 — tytez stavy ve stitcich noveho vzhledu. */
export const SLA_STATUS_BADGE_CLASS_A2: Readonly<Record<SlaStatus, string>> = {
  ok: 'rh-a2-badge--ok',
  blizi_se: 'rh-a2-badge--wait',
  prosle: 'rh-a2-badge--bad',
  none: '',
};

/**
 * Krok TAB1 body 6/7/9 — „STOJÍ DÉLE NEŽ 30 DNÍ“ (hlavicka, cerveny filtr,
 * cervene cislo na karte). Krok AS3: podminka zije v `@reos/today-metrics`,
 * protoze ji tytez slovy pocita i kontrola Asistentky `reos.run_checks`
 * (`case_stuck`) — jedna funkce, ne dve kopie, ktere by se rozesly.
 */
import { daysInStage, STAGE_STUCK_DAYS, isStuckInStage } from '@reos/today-metrics';

export { daysInStage, STAGE_STUCK_DAYS, isStuckInStage };
